import { FormGroup, UntypedFormGroup } from '@angular/forms';
import {
  FormlyFormBuilder,
  FormlyFieldConfig,
  FormlyConfig,
} from '@ngx-formly/core';
import { TreeNode } from 'primeng/api';
import { FormlyView } from './Data/FormlyView';
import { Art57Node } from './Node/Art57Node/Art57Node';
import { ErrorType } from './Node/ErrorType';
import { Injector } from '@angular/core';
import { Art57Section } from './Art57Section/Art57Section';
import { BaseNode } from './Node/BaseNode';

export class ErrorControlUtil {
  private static readonly DUPLICATE_MESSAGE = (labelNodeName: string) =>
    `This ${labelNodeName} is already present in the same scope`;

  private static buildError(
    node: Art57Node,
    errorActive: boolean,
    errorIdentifier: string,
    personalizedErrorMsg?: string,
  ): any {
    return {
      node,
      errorID: node.key + '-' + errorIdentifier,
      errorType: errorActive
        ? ErrorType.bussinessValidationError
        : ErrorType.errorInactive,
      controlId: undefined,
      controlLabel: '',
      error:
        errorActive == false
          ? ''
          : personalizedErrorMsg
            ? personalizedErrorMsg
            : ErrorControlUtil.DUPLICATE_MESSAGE(node.labelNodeName),
    };
  }

  static validateBusinessRules(node: Art57Node, nodeErrors: any) {
    const businessRulesErrors = node.validateBusinessRules();
    nodeErrors.push(...businessRulesErrors);
  }

  static collectAndMergeErrors(existingErrors: any[], newErrors: any[]) {
    let otherErrors: any[] = [];
    newErrors.forEach((error: any) => {
      const locatedErrorIndex = existingErrors.findIndex(
        (existingError: any) => existingError.errorID === error.errorID,
      );
      if (locatedErrorIndex != -1) {
        existingErrors[locatedErrorIndex] = error;
      } else {
        otherErrors = otherErrors.concat(error);
      }
    });
    return existingErrors.concat(otherErrors);
  }

  /**
   * Recursively enables or disables all Formly fields and their FormControls.
   *
   * @param nodes - Array of TreeNode elements to process.
   * @param formlyBuilder - Instance of FormlyFormBuilder used to build forms.
   * @param disableForm - If true, disables the fields; if false, enables them.
   */
  static toggleForms(
    nodes: TreeNode[],
    formlyBuilder: FormlyFormBuilder,
    disableForm: boolean = true,
  ): void {
    for (let node of nodes) {
      const nodeData = node.data;

      if (nodeData && nodeData instanceof FormlyView) {
        const form = new FormGroup({});
        const formlyFields: FormlyFieldConfig[] =
          nodeData.fields as FormlyFieldConfig[];

        // Build the form for this node
        formlyBuilder.buildForm(form, formlyFields, nodeData.model, {});

        // Recursively update all fields
        this.updateFieldState(formlyFields, disableForm);
      }

      // Process child nodes recursively
      if (node.children && node.children.length > 0) {
        this.toggleForms(node.children, formlyBuilder, disableForm);
      }
    }
  }

  /**
   * Recursively updates props.disabled and FormControl state for all fields.
   *
   * @param fields - Array of FormlyFieldConfig to update.
   * @param disable - If true, disables the field; if false, enables it.
   */
  private static updateFieldState(
    fields: FormlyFieldConfig[],
    disable: boolean,
  ): void {
    fields.forEach((field) => {
      // Update FormControl state if exists
      if (field.formControl) {
        disable
          ? field.formControl.disable({ emitEvent: false })
          : field.formControl.enable({ emitEvent: false });
      }

      // Update Formly props.disabled
      if (!field.props) {
        field.props = {};
      }
      field.props.disabled = disable;

      // Recursively process child fields
      if (field.fieldGroup && field.fieldGroup.length > 0) {
        this.updateFieldState(field.fieldGroup, disable);
      }
    });
  }

  static findErrors(
    injector: Injector,
    nodes: TreeNode[],
    formlyBuilder: FormlyFormBuilder,
    selectedNode: any,
    applyChildValidation: boolean = false,
  ): any[] {
    interface ControlSnapshot {
      disabled: boolean;
      readonly: boolean;
      required: boolean;
    }

    let errors: any = [];
    let formEnabled = true; // by default all forms are enabled
    for (let node of nodes) {
      /* To fix isse when enable/disable formly form with properties for operationtype and so */
      const snapshot = new Map<FormlyFieldConfig, ControlSnapshot>();
      const collect = (fields: FormlyFieldConfig[]) => {
        fields.forEach((field) => {
          if (field.props) {
            snapshot.set(field, {
              disabled: field.props?.disabled ? field.props?.disabled : false,
              readonly: field.props?.readonly ? field.props.readonly : false,
              required: field.props?.required ? field.props.required : false,
            });
          }
          if (field.fieldGroup?.length) {
            collect(field.fieldGroup);
          }
        });
      };

      let nodeData = node.data;
      let form = new FormGroup({});
      let nodeErrors: any = [];

      if (nodeData && node.data instanceof FormlyView) {
        let formlyFields: FormlyFieldConfig[] =
          nodeData.fields as FormlyFieldConfig[];

        collect(formlyFields);

        formlyBuilder.buildForm(form, formlyFields, nodeData.model, {});

        formEnabled = form.enabled;
        form.enable();
        this.markControlsAsTouchedAndDirty(form);
        form.markAllAsTouched();
        formlyFields.forEach((field) => {
          this.collectErrors(form, field, nodeErrors, node, null, injector);
        });
      }

      if (typeof (node as any)['validateBusinessRules'] === 'function') {
        this.validateBusinessRules(node as Art57Node, nodeErrors);
      }
      errors = errors.concat(nodeErrors);

      if (applyChildValidation && node.children && node.children.length > 0) {
        const detectedErrors = this.findErrors(
          injector,
          node.children,
          formlyBuilder,
          selectedNode,
          applyChildValidation,
        );
        errors = this.collectAndMergeErrors(errors, detectedErrors);
      }

      // this section of code is to
      if (
        node.parent &&
        typeof (node.parent as any)['validateBusinessRules'] === 'function'
      ) {
        const currentNodeParentValidateBusinessRulesMethod = (
          node.parent as Art57Node
        )['validateBusinessRules'];
        const baseClassValidateBusinessRulesMethod =
          Art57Node.prototype['validateBusinessRules'];

        if (
          currentNodeParentValidateBusinessRulesMethod !=
          baseClassValidateBusinessRulesMethod
        ) {
          let parentErrors: any = [];
          this.validateBusinessRules(node.parent as Art57Node, parentErrors);
          errors = this.collectAndMergeErrors(errors, parentErrors);
        }
      }

      node.data.valid =
        nodeErrors.length == 0 ||
        !nodeErrors.some(
          (error: any) => error.errorType != ErrorType.errorInactive,
        );

      /* To fix isse when enable/disable formly form with properties for operationtype and so */
      snapshot.forEach((savedProps, field) => {
        if (!field.props || !savedProps) {
          return;
        }

        field.props.readonly = savedProps.readonly;
        field.props.required = savedProps.required;
        field.props.disabled = savedProps.disabled;
      });

      if (!formEnabled) {
        form.disable();
      }
    }

    if (selectedNode && selectedNode.data && selectedNode.data.fields) {
      selectedNode!.data.fields = [...selectedNode!.data.fields]; // to fix selected node refreshlabel
    }

    return errors;
  }

  static collectErrors(
    formGroup: FormGroup,
    field: FormlyFieldConfig,
    errors: any[],
    node: TreeNode,
    parentField: FormlyFieldConfig | null,
    injector: Injector,
  ) {
    if (Array.isArray(field)) {
      field.forEach((nestedField) => {
        this.collectErrors(
          formGroup,
          nestedField,
          errors,
          node,
          parentField,
          injector,
        );
      });
    } else if (field.fieldGroup) {
      if (!field.key) {
        field.fieldGroup.forEach((nestedField) => {
          this.collectErrors(
            formGroup,
            nestedField,
            errors,
            node,
            field,
            injector,
          );
        });
      } else {
        const nestedFormGroup = formGroup.get(field.key as string) as FormGroup;
        if (nestedFormGroup) {
          this.markControlsAsTouchedAndDirty(nestedFormGroup);
          field.fieldGroup.forEach((nestedField) => {
            this.collectErrors(
              nestedFormGroup,
              nestedField,
              errors,
              node,
              field,
              injector,
            );
          });
        }
      }
    } else {
      const control = formGroup.get(field.key as string);
      if (control) {
        if (control.errors) {
          const errorMessages = this.getErrorMessages(
            control.errors,
            field,
            injector,
          );
          let errorType: string = ErrorType.standardFormlyError;
          if (control.errors.hasOwnProperty('lookupValidator')) {
            errorType = ErrorType.lookupFormlyError;
          }

          errors.push({
            node: node,
            errorID:
              node.key +
              '-' +
              (field.parent?.key
                ? field.parent!.key + '-' + field.key
                : field.key),
            errorType: errorType,
            controlId: field.id,
            controlLabel: field.props!.label,
            error: errorMessages,
          });
        } else {
          errors.push({
            node: node,
            errorID:
              node.key +
              '-' +
              (field.parent?.key
                ? field.parent!.key + '-' + field.key
                : field.key),
            errorType: ErrorType.errorInactive,
            controlId: field.id,
            controlLabel: field.props!.label,
            error: '',
          });
        }
      }
    }
  }

  static getErrorMessages(
    errors: any,
    field: FormlyFieldConfig,
    injector: Injector,
  ): string[] {
    let formlyConfig: FormlyConfig = injector.get(Art57Section).formlyConfig;
    const messages: string[] = [];
    for (const errorName in errors) {
      if (errors.hasOwnProperty(errorName)) {
        if (
          errorName === 'authorisationProcedure11' ||
          errorName === 'authorisationProcedure9' ||
          errorName === 'legalBasis8' ||
          errorName === 'legalBasis9'
        ) {
          const validationMessage = field.validators?.[errorName]?.message;
          if (typeof validationMessage === 'string') {
            messages.push(validationMessage);
          } else if (typeof validationMessage === 'function') {
            const message = validationMessage(errors[errorName], field);
            if (typeof message === 'string') {
              messages.push(message);
            } else {
              messages.push(message.toString());
            }
          }
        } else if (errorName === 'customValidator') {
          if (errors.customValidator.message instanceof Function) {
            messages.push(
              errors.customValidator.message(errors[errorName], field),
            );
          } else {
            messages.push(errors.customValidator.message);
          }
        } else if (errorName === 'inOptions') {
          if (errors.inOptions.message instanceof Function) {
            messages.push(errors.inOptions.message(errors[errorName], field));
          } else {
            messages.push(errors.inOptions.message);
          }
        } else if (errorName === 'lookupValidator') {
          if (errors.lookupValidator.message instanceof Function) {
            messages.push(
              errors.lookupValidator.message(errors[errorName], field),
            );
          } else {
            messages.push(errors.lookupValidator.message);
          }
        } else if (
          errorName === 'required' &&
          field.validation &&
          field.validation.messages &&
          field.validation.messages['required']
        ) {
          messages.push(field.validation.messages['required'].toString());
        } else {
          const validationMessage = formlyConfig.getValidatorMessage(errorName);
          if (typeof validationMessage === 'string') {
            messages.push(validationMessage);
          } else if (typeof validationMessage === 'function') {
            const message = validationMessage(errors[errorName], field);
            if (typeof message === 'string') {
              messages.push(message);
            }
          }
        }
      }
    }
    return messages;
  }

  static markControlsAsTouchedAndDirty(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      if (control) {
        control.markAsTouched();
        control.markAsDirty();
        control.updateValueAndValidity();
      }
    });
  }

  /**
   * Acces properly way to value nested node.data.model.
   * @path can be a string with separate by doths ("a.b.c") or a string array.
   * @Return undefined if any doth miss the property.
   */
  private static getNestedModelValue(
    node: TreeNode<any>,
    path: string | string[],
  ): any {
    const parts = Array.isArray(path) ? path : path ? path.split('.') : [];
    let current: any = node?.data?.model;
    for (const key of parts) {
      if (current == null) return undefined;
      current = current[key];
    }
    return current;
  }

  /**
   * Generic logic that checks whether another child of the same parent has the same value
   * in the path provided (path - relative to data.model).
   * If there is a duplicate, it returns an array with the error; if not, it returns [].
   */
  private static sameReferenceByPath(
    node: Art57Node,
    path: string | string[],
  ): boolean {
    // Early return if no there parent or children
    const children = node?.parent?.children;
    if (!children || children.length === 0) return false;
    const thisValue = ErrorControlUtil.getNestedModelValue(node, path);
    // used `some`
    const hasDuplicate = children.some((child) => {
      const childValue = ErrorControlUtil.getNestedModelValue(child, path);
      return child.key !== node.key && childValue === thisValue;
    });

    return hasDuplicate;
  }

  /**
   * Generic logic that checks whether another child of the same model Values
   * If there is a duplicate, it returns an array with the error; if not, it returns [].
   */
  private static sameModel(node: Art57Node): boolean {
    // Early return if no there parent or children
    const children = node?.parent?.children;
    if (!children || children.length === 0) return false;

    const { num, localnumber, ...nodeModelValues } = node.getMessage();
    const hasDuplicate = children.some((child) => {
      if (node.key == child.key) {
        return false;
      }
      const { num, localnumber, ...childModelValues } = (child as Art57Node).getMessage();
      return ErrorControlUtil.JsonDeepCompare(
        nodeModelValues,
        childModelValues,
      );
    });

    return hasDuplicate;
  }

  public static JsonDeepCompare(a: any, b: any): boolean {
    // Both are arrays
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;

      // Sort arrays by string representation to ignore order
      const sortedA = [...a].sort((x, y) => String(x).localeCompare(String(y)));
      const sortedB = [...b].sort((x, y) => String(x).localeCompare(String(y)));

      return sortedA.every((val, idx) =>
        ErrorControlUtil.JsonDeepCompare(val, sortedB[idx]),
      );
    }

    // Both are objects
    if (a && b && typeof a === 'object' && typeof b === 'object') {
      const keysA = Object.keys(a).sort();
      const keysB = Object.keys(b).sort();

      if (
        keysA.length !== keysB.length ||
        !keysA.every((k, i) => k === keysB[i])
      ) {
        return false;
      }

      return keysA.every((k) => ErrorControlUtil.JsonDeepCompare(a[k], b[k]));
    }

    // Compare primitive values as strings (ignore type differences)
    return String(a) === String(b);
  }

  /** Public methods for reference validation */
  static sameReferenceByValue(node: Art57Node, property: string): any {
    const errorActive = ErrorControlUtil.sameReferenceByPath(node, property);
    return [
      ErrorControlUtil.buildError(node, errorActive, 'sameReferenceByPath'),
    ];
  }

  static sameReferenceByValues(
    node: Art57Node,
    properties: string[],
    personalizedErrorMsg?: string,
  ): any {
    let allPropertiesSameReference = true;
    for (const property of properties) {
      allPropertiesSameReference =
        allPropertiesSameReference &&
        ErrorControlUtil.sameReferenceByPath(node, [property, 'value']);
      if (!allPropertiesSameReference) {
        break;
      }
    }

    return [
      ErrorControlUtil.buildError(
        node,
        allPropertiesSameReference,
        'sameReferenceByValues',
        personalizedErrorMsg,
      ),
    ];
  }

  static sameReferenceByChild(
    node: Art57Node,
    propertyParent: string,
    propertyChild: string,
  ): any {
    return [ErrorControlUtil.buildError(node, false, 'sameReferenceByChild')];
    // JMBL by now we ignore this Bussiness Rule
    const hasErrors = ErrorControlUtil.sameReferenceByPath(node, [
      propertyParent,
      propertyChild,
    ]);
    return [
      ErrorControlUtil.buildError(node, hasErrors, 'sameReferenceByChild'),
    ];
  }

  static sameModelValues(node: Art57Node): any {
    const personalizedErrorMsg =
      node.labelNodeName +
      ' entity is duplicated in ' +
      (node.parent as BaseNode).labelNodeName;
    const hasErrors = ErrorControlUtil.sameModel(node);
    return [
      ErrorControlUtil.buildError(
        node,
        hasErrors,
        'sameModelValues',
        personalizedErrorMsg,
      ),
    ];
  }

  static findFieldErrors(
    fields: FormlyFieldConfig[] | FormlyFieldConfig,
    form: UntypedFormGroup,
    injector: Injector,
  ): any[] {
    const fieldArray = Array.isArray(fields) ? fields : [fields];
    const errors: any[] = [];

    /**
     * Marks only the controls that belong to visible fields.
     * This avoids touching hidden or not-yet-rendered fields.
     */
    const markVisibleControls = (
      formGroup: UntypedFormGroup,
      field: FormlyFieldConfig,
    ) => {
      // If the field is a group, recursively process its children
      if (field.fieldGroup && field.fieldGroup.length > 0) {
        const nestedGroup = field.key
          ? (formGroup.get(field.key as string) as UntypedFormGroup)
          : formGroup;

        field.fieldGroup.forEach((child) =>
          markVisibleControls(nestedGroup, child),
        );
        return;
      }

      // Skip fields without a key (template fields, structural fields)
      if (!field.key) return;

      // Resolve the existing FormControl
      const control = formGroup.get(field.key as string);
      if (!control) return; // Control does not exist yet

      // Mark only visible fields as touched/dirty
      if (field.hide !== true) {
        control.markAsTouched();
        control.markAsDirty();
        control.updateValueAndValidity({ emitEvent: true });
      }
    };

    /**
     * Recursively inspects fields and collects validation errors.
     */
    const collectErrorsRecursively = (
      formGroup: UntypedFormGroup,
      field: FormlyFieldConfig,
      parent: FormlyFieldConfig | null,
    ) => {
      // If this is a fieldGroup, recursively process children
      if (field.fieldGroup && field.fieldGroup.length > 0) {
        const nestedGroup = field.key
          ? (formGroup.get(field.key as string) as UntypedFormGroup)
          : formGroup;

        field.fieldGroup.forEach((f) =>
          collectErrorsRecursively(nestedGroup, f, field),
        );
        return;
      }

      // Skip fields without controls
      if (!field.key) return;

      const control = formGroup.get(field.key as string);
      if (!control) return;

      // Collect error messages
      if (control.errors) {
        const msgs = ErrorControlUtil.getErrorMessages(
          control.errors,
          field,
          injector,
        );

        errors.push({
          errorID: field.id,
          controlId: field.id,
          controlLabel: field.props?.label || '',
          errorType: ErrorType.standardFormlyError,
          error: msgs,
        });
      } else {
        errors.push({
          errorID: field.id,
          controlId: field.id,
          controlLabel: field.props?.label || '',
          errorType: ErrorType.errorInactive,
          error: '',
        });
      }
    };

    // Step 1: mark visible controls so Angular triggers all required validations
    fieldArray.forEach((f) => markVisibleControls(form, f));

    // Step 2: collect actual validation errors
    fieldArray.forEach((f) => collectErrorsRecursively(form, f, null));

    return errors;
  }
}
